import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography,
} from '@mui/material';
import configurationApi from '../../services/configurationApi';
import { useConfirmModal } from '../../components/shared/ConfirmModal';
import {
  PIPER_DECLINE_CONFIRM_MESSAGE,
  PIPER_DECLINE_CONFIRM_TITLE,
  PIPER_TERMS_SECTIONS,
  PIPER_TERMS_TITLE,
} from '../../content/piperTerms';

/**
 * Piper terms acknowledgement dialog. Accept and Decline persist a choice.
 * Cancel, the close button, Escape, and backdrop click dismiss without recording one.
 */
const PiperTermsModal = ({ open, onAccepted, onClose }) => {
  const [recordPiperTerms] = configurationApi.endpoints.recordPiperTerms.useMutation();
  const { confirm, ConfirmModalContainer } = useConfirmModal();
  const [busy, setBusy] = useState(false);

  const handleDismiss = () => {
    if (busy) return;
    onClose?.();
  };

  const handleAccept = async () => {
    setBusy(true);
    try {
      await recordPiperTerms({ accepted: true }).unwrap();
      onAccepted?.();
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    const confirmed = await confirm({
      title: PIPER_DECLINE_CONFIRM_TITLE,
      message: PIPER_DECLINE_CONFIRM_MESSAGE,
      confirmText: 'Decline and disable Piper',
      cancelText: 'Go back',
      confirmColor: 'error',
    });
    if (!confirmed) return;

    setBusy(true);
    try {
      await recordPiperTerms({ accepted: false }).unwrap();
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        onClose={handleDismiss}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <span>{PIPER_TERMS_TITLE}</span>
          <IconButton
            aria-label="Close"
            onClick={handleDismiss}
            disabled={busy}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {PIPER_TERMS_SECTIONS.map((section, index) => (
              <Box key={section.heading || `intro-${index}`}>
                {section.heading && (
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    {section.heading}
                  </Typography>
                )}
                {section.body.split('\n\n').map((paragraph) => (
                  <Typography
                    key={paragraph.slice(0, 48)}
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, '&:last-child': { mb: 0 }, whiteSpace: 'pre-wrap' }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDismiss} disabled={busy} color="inherit" sx={{ mr: 'auto' }}>
            Cancel
          </Button>
          <Button onClick={handleDecline} disabled={busy} color="inherit">
            Decline
          </Button>
          <Button onClick={handleAccept} disabled={busy} variant="contained">
            {busy ? 'Saving…' : 'Accept and enable Piper'}
          </Button>
        </DialogActions>
      </Dialog>
      {ConfirmModalContainer}
    </>
  );
};

export default PiperTermsModal;
